import type { JSX, MouseEventHandler, ReactNode } from 'react';
import type { Interpolation } from 'styled-components';
import styled, { css } from 'styled-components';

import Label from 'components/core/typography/Label';
import { ChevronRightIcon } from 'components/ui/icons';
import { FillWithClickable } from 'components/ui/shared/Buttons/Clickable';

import { BODY_TEXT, DIVIDER } from 'styles/color';
import { SECTION_PADDING } from 'styles/spacing';
import { FONT_SIZE_14, LETTER_SPACING_DEFAULT, NEUTRAL_050, SPACE_200, SPACE_300, SPACE_700 } from 'styles/tokens';

export const ListSection = styled.section<{ disabled?: boolean }>`
  border-bottom: 1px solid ${DIVIDER};
  padding: 15px 17px;
  position: relative;
  width: 100%;

  > * {
    opacity: ${props => (props.disabled ? 0.5 : 1)};
  }

  && * {
    ${props =>
      props.disabled
        ? css`
            pointer-events: none;
          `
        : ''}
  }

  /** Only add margin-bottom to ListSection children that are NOT last-child, or buttons */
  > :not(:last-child, button) {
    margin-bottom: 14px;
  }
`;

export const ListSectionTitle = styled(Label)<{ $isLeadingIcon?: boolean }>`
  align-items: center;
  display: flex;
  ${({ $isLeadingIcon }) =>
    !$isLeadingIcon &&
    css`
      justify-content: space-between;
    `}
  ${({ $isLeadingIcon }) =>
    $isLeadingIcon &&
    css`
      gap: ${SPACE_200};
    `}
`;

export const ListSearchSection = styled(ListSection)`
  padding: ${SPACE_300};

  &:last-child {
    border-bottom: none;
  }
`;

export const ListSectionItemTitle = styled(ListSectionTitle)<{ $isLeadingIcon?: boolean }>`
  font-size: ${FONT_SIZE_14};
  font-weight: normal;
  letter-spacing: ${LETTER_SPACING_DEFAULT};
  pointer-events: none;
  text-transform: none;

  svg {
    fill: ${BODY_TEXT};
  }
`;

const ListSectionItem = styled(ListSection)`
  padding: ${SECTION_PADDING};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${NEUTRAL_050};
  }
`;

export interface ListItemType {
  /**
   * Additional data associated with the list item
   */
  additionalData?: Record<string, any>;
  /**
   * Optional CSS class name for the list item
   */
  className?: string;
  /**
   * Optional icon to display alongside the list item
   */
  icon?: ReactNode;
  /**
   * Whether the icon (if provided) precedes or follows the label
   */
  iconPosition?: 'leading' | 'trailing';
  /**
   * The main label text for the list item
   */
  label: string;
  /**
   * Optional React element to use instead of the label string
   */
  labelElement?: JSX.Element;
  /**
   * Optional React element to completely override the ListSectionItem container
   */
  listItemElementOverride?: JSX.Element;
  /**
   * Optional click event handler for the list item
   */
  onClick?: MouseEventHandler<HTMLButtonElement>;
  /**
   * Optional mouse enter handler for the list item
   */
  onMouseEnter?: () => void;
  /**
   * Optional test ID for testing purposes
   */
  testId?: string;
  /**
   * Custom css for ListItem title
   */
  titleCss?: Interpolation<object>;
}

export const ListItem = ({
  className,
  icon = (
    <ChevronRightIcon
      css={css`
        margin-left: ${SPACE_700};
      `}
    />
  ),
  iconPosition = 'trailing',
  label,
  labelElement,
  listItemElementOverride,
  onClick,
  onMouseEnter,
  testId,
  titleCss,
}: ListItemType) => {
  if (listItemElementOverride) {
    return listItemElementOverride;
  }

  return (
    <ListSectionItem className={className} data-testid={testId} onMouseEnter={onMouseEnter}>
      <FillWithClickable onClick={onClick} />
      <ListSectionItemTitle $isLeadingIcon={icon ? iconPosition === 'leading' : undefined} css={titleCss}>
        {icon && iconPosition === 'leading' ? icon : null}
        {labelElement || label}
        {icon && iconPosition === 'trailing' ? icon : null}
      </ListSectionItemTitle>
    </ListSectionItem>
  );
};

export type CommonListItem = ListItemType & { id: string };

interface ListItemsProps {
  /**
   * An array of CommonListItem objects to be displayed.
   */
  items: CommonListItem[];
  /**
   * An optional React component used to render each list item. Defaults to ListItem.
   */
  renderListItem?: (props: ListItemType) => JSX.Element;
  /**
   * An optional search query string to filter the list items.
   */
  searchQuery?: string;
}

export const ListItems = ({ items, renderListItem: RenderListItem = ListItem, searchQuery = '' }: ListItemsProps) => (
  <>
    {items.map(
      item =>
        item.label.toLowerCase().includes(searchQuery.toLowerCase()) && (
          <RenderListItem
            additionalData={item.additionalData}
            icon={item.icon}
            iconPosition={item.iconPosition}
            key={item.id}
            label={item.label}
            labelElement={item.labelElement}
            listItemElementOverride={item.listItemElementOverride}
            onClick={item.onClick}
            onMouseEnter={item.onMouseEnter}
            testId={item.testId}
          />
        )
    )}
  </>
);
